// Projects an STSAssetState's master-timeline + take buffers into a
// synthetic Block[] shape that the per-asset upload orchestrator
// (submitAssetUpload) accepts. STS state lives in a separate slice
// (stsAssets[idx].master + .takes) because the recording-booth model
// diverged from TTS, but for upload purposes the orchestrator only
// needs an iterable of "things to upload as audio" + the timeline
// offsets — so we synthesize Blocks with a single fav-take each.
//
// Field-name notes (verified against shared/types.ts:124-146):
//   - STSTake carries `audioBuffer` (not `buffer`) and has no `model`.
//   - MasterClip uses `inPoint` / `outPoint` / `offset` (not start/end).
//     Clip duration is `outPoint - inPoint`.
//   - STSTake.params is the STS-shaped {stability, similarity, style,
//     speakerBoost, removeNoise} record — passed through as the synthetic
//     Take's paramsSnapshot so buildParamsTxt's STS branch reads the right
//     numbers (it dispatches via the orchestrator's `isSts` flag).
//
// Pure function — no store coupling.

import type { Block, Take, STSAssetState, TtsParams } from '@shared/types';
import { TTS_PARAMS_V2_DEFAULT, TTS_PARAMS_V4_DEFAULT } from '@shared/constants';

function defaultTtsParams(): TtsParams {
  return {
    model: 'v2',
    v2: { ...TTS_PARAMS_V2_DEFAULT },
    v4: { ...TTS_PARAMS_V4_DEFAULT },
  };
}

export function stsAssetToBlocks(
  state: STSAssetState | undefined | null,
): Block[] {
  if (!state || !state.master?.length) return [];
  const blocks: Block[] = [];
  for (const clip of state.master) {
    // Resolve the underlying take by id. If the clip references a missing
    // take (shouldn't happen but guard anyway), skip.
    const take = state.takes.find((t) => t.id === clip.takeId);
    if (!take) continue;
    const clipDuration = Math.max(0, (clip.outPoint ?? 0) - (clip.inPoint ?? 0));
    const synthTake: Take = {
      id: take.id,
      // The orchestrator only reads `buffer` (for the upload payload) and
      // `duration` (for total).
      buffer: take.audioBuffer,
      duration: clipDuration > 0 ? clipDuration : take.duration,
      // model is read by buildParamsTxt only on the TTS branch — the STS
      // branch ignores it. Use 'v2' as a stable default.
      model: 'v2',
      // STS take params drive the params.txt STS lines. paramsSnapshot is
      // typed TtsParamsV2 | TtsParamsV4 in React, but uploadDocs reads it
      // tolerantly via a RawParams shape that includes stability/similarity/
      // style/speakerBoost/removeNoise — STSParams matches that shape.
      paramsSnapshot: take.params as unknown as Take['paramsSnapshot'],
      voiceId: take.voiceId,
    };
    blocks.push({
      // Synthetischer Block pro MasterClip — die Clip-ID ist stabil und
      // eindeutig, also die natürliche Block-ID.
      id: clip.id,
      text: '',                 // STS has no per-block text
      params: defaultTtsParams(),
      takes: [synthTake],
      favIdx: 0,
      takeCounter: 1,
      locked: false,
      offset: clip.offset ?? 0,
      pinned: false,
      voiceId: take.voiceId,
      // Surface the underlying take's session/takeInSession so the upload
      // pipeline's buildStsBlockFilename can emit `Take {s}-{n}.mp3`.
      _stsTakeMeta: {
        session: take.session ?? 1,
        takeInSession: take.takeInSession ?? state.takes.indexOf(take) + 1,
        inPoint: clip.inPoint ?? 0,
        outPoint: clip.outPoint ?? take.duration,
        fullDuration: take.duration,
      },
    });
  }
  return blocks;
}
